import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { CheckCircle, ArrowRight, FileText, BookOpen, MessageCircle, Users, ExternalLink } from 'lucide-react';

interface SuccessStepProps {
  onComplete: () => void;
  onBack: () => void;
  onboardingData: {
    siteId?: string;
    siteName?: string;
    languages?: string[];
  };
}

const SuccessStep: React.FC<SuccessStepProps> = ({
  onComplete,
  onboardingData,
}) => {
  const accomplishments = [
    {
      title: 'Created your first site',
      description: `${onboardingData.siteName || 'Site'} is ready for translations`
    },
    {
      title: 'Selected target languages',
      description: `${onboardingData.languages?.length || 0} language${onboardingData.languages?.length === 1 ? '' : 's'} configured`
    },
    {
      title: 'Integration ready',
      description: 'SDK installed and configured for your site'
    },
    {
      title: '14-day free trial activated',
      description: 'Full access to all features, no credit card required'
    }
  ];

  const nextSteps = [
    {
      to: `/sites/${onboardingData.siteId}/translations`,
      icon: FileText,
      title: 'Manage Translations',
      description: 'Review and edit your translations'
    },
    {
      to: '/glossaries',
      icon: BookOpen,
      title: 'Create Glossary',
      description: 'Define consistent terminology'
    },
    {
      to: '/translation-memory',
      icon: MessageCircle,
      title: 'Translation Memory',
      description: 'Reuse approved translations'
    },
    {
      to: '/team',
      icon: Users,
      title: 'Invite Team',
      description: 'Collaborate with translators'
    }
  ];

  const resources = [
    { href: '/docs', label: 'Documentation', icon: '📖' },
    { href: '/docs/api', label: 'API Reference', icon: '🔌' },
    { href: '/support', label: 'Support', icon: '💬' },
    { href: '/community', label: 'Community', icon: '👨‍👩‍👧‍👦' }
  ];

  return (
    <CardContent className="p-12 text-center">
      {/* Success Icon */}
      <div className="mb-8">
        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-green-400 to-primary rounded-full text-white text-6xl animate-bounce">
          🎉
        </div>
      </div>

      {/* Title */}
      <h1 className="text-4xl font-bold text-foreground mb-4">
        You're All Set!
      </h1>

      <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
        Congratulations! You've successfully set up LocalizationHub. Your site is
        ready to go global.
      </p>

      {/* Completion Checklist */}
      <Card className="bg-gradient-to-r from-green-50 to-primary/5 border-0 mb-8 max-w-2xl mx-auto">
        <CardContent className="p-8">
          <h3 className="text-lg font-semibold text-foreground mb-6">
            What You've Accomplished:
          </h3>
          <div className="space-y-4 text-left">
            {accomplishments.map((item, index) => (
              <div key={index} className="flex items-start">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mr-3" />
                <div>
                  <p className="font-medium text-foreground">{item.title}</p>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Next Steps */}
      <Card className="border-2 mb-8 max-w-2xl mx-auto">
        <CardContent className="p-8">
          <h3 className="text-lg font-semibold text-foreground mb-6">
            Recommended Next Steps:
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {nextSteps.map((step, index) => (
              <Link
                key={index}
                to={step.to}
                className="p-4 border rounded-lg hover:border-primary hover:shadow-md transition text-left group"
              >
                <step.icon className="h-8 w-8 text-primary mb-2 group-hover:scale-110 transition-transform" />
                <h4 className="font-semibold text-foreground mb-1">
                  {step.title}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {step.description}
                </p>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Resources */}
      <Card className="bg-muted/30 border-0 mb-8 max-w-2xl mx-auto">
        <CardContent className="p-6">
          <h4 className="font-semibold text-foreground mb-4">Helpful Resources:</h4>
          <div className="flex justify-center flex-wrap gap-4 text-sm">
            {resources.map((resource, index) => (
              <a
                key={index}
                href={resource.href}
                className="text-primary hover:underline font-medium flex items-center"
              >
                <span className="mr-1">{resource.icon}</span>
                {resource.label}
              </a>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* CTA Button */}
      <Button
        onClick={onComplete}
        size="lg"
        className="text-lg px-10 py-6 bg-gradient-to-r from-green-500 to-primary hover:from-green-600 hover:to-primary/90 shadow-lg mb-4"
      >
        Go to Dashboard
        <ArrowRight className="h-5 w-5 ml-2" />
      </Button>

      <p className="text-sm text-muted-foreground">
        Welcome to LocalizationHub! We're excited to help you go global.
      </p>
    </CardContent>
  );
};

export default SuccessStep;
